import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import DOMPurify from 'dompurify';
import { fetchFastChannels, fetchLiveChannels } from '../LiveChannels/service';
import { STATIC_CHANNELS_DATA } from './flsChannelStaticData';
import Loading from '../../Components/Loading/Loading';
import './scss/_flsChannelDetail.scss';

const getSlug = (str) => {
  if (!str) return '';
  return str
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

const FlsChannelDetail = () => {
  const { vanityUrl } = useParams();
  const navigate = useNavigate();

  const projectInfo = useSelector((state) => state?.projectInfo?.value);
  const accessToken = useSelector((state) => state?.accessToken?.value);

  const [channelDetails, setChannelDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showHeroVideo, setShowHeroVideo] = useState(false);

  const currentSlug = useMemo(() => getSlug(vanityUrl), [vanityUrl]);

  useEffect(() => {
    window.scrollTo(0, 0);

    const resetScrollAfterHistoryRestore = window.setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);

    return () => window.clearTimeout(resetScrollAfterHistoryRestore);
  }, [vanityUrl]);

  // 1. Dynamic staticInfo matching using useMemo
  const staticInfo = useMemo(() => {
    return STATIC_CHANNELS_DATA.find((item) => {
      if (item.channel_page_url && currentSlug && item.channel_page_url.toLowerCase().includes(currentSlug)) return true;
      if (currentSlug && getSlug(item.channel_name) === currentSlug) return true;
      if (currentSlug && currentSlug.length > 2 && getSlug(item.channel_name).includes(currentSlug)) return true;
      if (currentSlug && currentSlug.length > 2 && currentSlug.includes(getSlug(item.channel_name))) return true;
      return false;
    });
  }, [currentSlug]);

  useEffect(() => {
    setShowHeroVideo(false);

    if (loading || !staticInfo?.youtube_video_id) return undefined;

    const videoTimer = window.setTimeout(() => {
      setShowHeroVideo(true);
    }, 2000);

    return () => window.clearTimeout(videoTimer);
  }, [currentSlug, loading, staticInfo?.youtube_video_id]);

  useEffect(() => {
    let active = true;
    const appInfo = { projectDetails: projectInfo, accessToken };

    const loadChannelData = async () => {
      setLoading(true);
      setError(false);
      setChannelDetails(null);

      let targetChannelId = staticInfo?.channel_id;

      // If still no channel_id, search via all live channels API
      if (!targetChannelId) {
        try {
          const liveRes = await fetchLiveChannels(appInfo);
          const liveChannels = liveRes?.data?.data?.channels || liveRes?.data?.channels || [];
          const matchedLive = liveChannels.find(
            (ch) =>
              getSlug(ch.channel_name) === currentSlug ||
              String(ch.id) === currentSlug ||
              (ch.vanity_url && getSlug(ch.vanity_url) === currentSlug)
          );
          if (matchedLive) {
            targetChannelId = matchedLive.id || matchedLive.channel_id;
          }
        } catch (e) {
          console.error('Failed to resolve channel slug from live channels', e);
        }
      }

      if (!targetChannelId) {
        if (currentSlug.includes('pac-12') || currentSlug.includes('pac12')) {
          targetChannelId = 2067;
        } else if (currentSlug.includes('barca') || currentSlug.includes('top-barca')) {
          targetChannelId = 2018;
        } else if (currentSlug.includes('nascar')) {
          targetChannelId = 2050;
        }
      }

      if (targetChannelId) {
        try {
          const res = await fetchFastChannels(appInfo, targetChannelId);
          const rawData = res?.data?.data || res?.data;
          const detailsObj = Array.isArray(rawData) ? rawData[0] : rawData;

          if (active && detailsObj) {
            setChannelDetails(detailsObj);
          } else if (active) {
            setError(true);
          }
        } catch (err) {
          if (active) setError(true);
        }
      } else if (active) {
        setLoading(false);
      }

      if (active) setLoading(false);
    };

    loadChannelData();

    return () => {
      active = false;
    };
  }, [currentSlug, accessToken, projectInfo, staticInfo?.channel_id]);

  const displayName =
    channelDetails?.channel_name ||
    staticInfo?.channel_name ||
    vanityUrl?.replace(/-/g, ' ').toUpperCase() ||
    'CHANNEL';

  useEffect(() => {
    document.title = `Stream ${displayName} Free - Watch Online | Free Live Sports TV`;
  }, [displayName]);

  const displayDescription =
    channelDetails?.seo_description_html ||
    channelDetails?.seo_description ||
    channelDetails?.description ||
    staticInfo?.description ||
    `Watch ${displayName} - the FREE streaming channel on Free Live Sports TV. Stream nonstop sports games, highlights, predictions, analysis and iconic sporting moments 24/7.`;

  const heroBg =
    staticInfo?.bg_image_url ||
    channelDetails?.background_image ||
    channelDetails?.banner ||
    staticInfo?.thumbnail_image_url ||
    channelDetails?.logo ||
    channelDetails?.logo_thumb ||
    'https://gizmeon.mdc.akamaized.net/files/web/images/fls/uploads/2025/04/pac12-bg.jpg';

  const logoImg =
    channelDetails?.logo ||
    channelDetails?.logo_thumb ||
    staticInfo?.thumbnail_image_url ||
    'https://gizmeon.mdc.akamaized.net/files/web/images/fls/static/pac-12-logo-sml.png';

  const activeChannelId =
    channelDetails?.channel_id ||
    channelDetails?.id ||
    staticInfo?.channel_id;

  const handleStreamClick = () => {
    if (activeChannelId) {
      navigate(`/live-channels?c=${activeChannelId}`);
    } else {
      navigate('/live-channels');
    }
  };

  const isPosterImage =
    staticInfo?.thumbnail_image_url?.includes('683x1024') ||
    staticInfo?.thumbnail_image_url?.includes('reduced') ||
    staticInfo?.thumbnail_image_url?.includes('avatar') ||
    staticInfo?.thumbnail_image_url?.includes('channels-');

  const shouldBlurBg = !staticInfo?.bg_image_url && isPosterImage;

  const bannerImg = isPosterImage ? staticInfo.thumbnail_image_url : logoImg;

  if (loading) {
    return <Loading />;
  }

  return (
    <main className="flsChannelDetailPage">
      {/* Hero Section */}
      <section className="channelDetailHero">
        {/* Hero Background Layer */}
        <div className={`heroBgLayer ${showHeroVideo ? 'hasVideo' : ''}`}>
          {showHeroVideo && staticInfo?.youtube_video_id && (
            <iframe
              className="heroBgVideo"
              src={`https://www.youtube-nocookie.com/embed/${staticInfo.youtube_video_id}?autoplay=1&mute=1&controls=0&loop=1&playlist=${staticInfo.youtube_video_id}&modestbranding=1&rel=0`}
              title={`${displayName} video background`}
              allow="autoplay; encrypted-media"
              aria-hidden="true"
            />
          )}
          <img
            src={heroBg}
            alt=""
            className={`heroBgImage ${shouldBlurBg ? 'isBlur' : ''}`}
          />
          <div className="heroOverlay"></div>
        </div>

        <div className="heroContainer">
          <div className="heroLeft">
            <h1 className="channelHeroTitle">Watch {displayName}</h1>
            <div
              className="channelHeroDesc"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(displayDescription) }}
            />
            <button
              className="channelHeroCta"
              onClick={handleStreamClick}
              type="button"
              aria-label={`Stream ${displayName} Free`}
            >
              STREAM {displayName.toUpperCase()} FREE
              <span className="playCircle">▶</span>
            </button>
          </div>

          <div className="heroRight">
            <div className={`channelLogoCard ${isPosterImage ? 'isPosterCard' : ''}`}>
              <img src={bannerImg} alt={`${displayName}`} />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default FlsChannelDetail;
